import React, { useEffect, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import ProfileBackground from '../../components/overview/ProfileBackground'
import ProfilePhoto from '../../components/overview/ProfilePhoto'
import BrowseFilter from '../../components/cards/filter/BrowseFilter'
import CardsContainer from '../../components/cards/CardsContainer'
import BrowseCard from '../../components/cards/card/BrowseCard'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import Button from '../../components/ui/Button'
import BackButton from '../../components/common/BackButton'
import { MapPinIcon, ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline'
import {
  useGetMerchantFullProfileQuery,
  useFollowUserMutation
} from '../../redux/apis/endpoints/profileApi'
import { useUrlChangeHandler } from '../../hooks/useUrlChangeHandler'
import { handleUrlChange, clearAll } from '../../redux/slices/filterSlice'
import UserRatingDisplay from '../../components/common/UserRatingDisplay'

/**
 * MerchantPage component displays a merchant's profile and products
 * URL: /merchant/:id
 *
 * Features:
 * - Profile information (pic, name, location, rating)
 * - Follow/unfollow functionality
 * - Followers/following stats
 * - Browsable products from the merchant filtered via Algolia
 */
const MerchantPage = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const mountedRef = useRef(false)
  const history = useHistory()

  // Handle URL changes
  useUrlChangeHandler()

  // Fetch merchant profile data using the dedicated endpoint
  const {
    data: merchant,
    isLoading: isLoadingMerchant,
    error: merchantError
  } = useGetMerchantFullProfileQuery(id)

  // Follow/unfollow mutation
  const [followUser, { isLoading: isFollowing }] = useFollowUserMutation()

  // Auth state from Redux
  const auth = useSelector(state => state.auth)
  const isLoggedIn = auth.isAuthenticated
  const currentUser = auth.user

  // Get product list from filter state (same as BrowsePage)
  const {
    products,
    loading,
    dataLoading,
    error,
    hasMore,
    totalResults,
    searchTerm,
    isInitialized,
    lastSearch
  } = useSelector(state => state.filter)

  // Handle component lifecycle and trigger search
  useEffect(() => {
    console.log('MerchantPage component mounting with ID:', id);
    
    if (!mountedRef.current) {
      mountedRef.current = true
      
      // Only clear if no products are loaded yet
      if (!products.length) {
        console.log('Clearing previous products');
        dispatch(clearAll())
      }
      
      // Force a new search with the current URL parameters
      console.log('Dispatching handleUrlChange to trigger Algolia search');
      dispatch(handleUrlChange())
    }
    
    return () => {
      if (mountedRef.current) {
        mountedRef.current = false
      }
    }
  }, [dispatch, id, products.length])

  // Handle follow/unfollow
  const handleFollowClick = async () => {
    if (!isLoggedIn) {
      // Redirect to login or show login modal
      history.push('/login');
      return
    }

    try {
      await followUser(id)
    } catch (error) {
      console.error('Error following merchant:', error)
    }
  }

  // Handle message click
  const handleMessageClick = () => {
    if (!isLoggedIn) {
      history.push('/login');
      return;
    }
    // Navigate to the chat page with the merchant
    // MessagePage will use params.chatId to identify the chat partner.
    // We pass merchant info in state for a better UX on MessagePage load.
    history.push(`/message/${merchant.id}`, {
      merchantId: merchant.id,
      merchantName: merchant.username,
      avatarUrl: merchant.profilePhoto?.url || merchant.avatar?.url
    });
  };

  // Loading state
  if (isLoadingMerchant) {
    return <LoadingSpinner />
  }

  // Error state
  if (merchantError) {
    return (
      <div className='p-6 text-center text-red-500'>
        Error loading merchant profile: {merchantError.message}
      </div>
    )
  }

  // Not found state
  if (!merchant) {
    return (
      <div className='p-6 text-center text-gray-500'>Merchant not found</div>
    )
  }

  // Get default address from merchant data (provided by backend)
  const merchantLocation = merchant.defaultAddress

  // Get if current user is following the merchant (provided by backend)
  const isCurrentUserFollowing = merchant.isFollowing

  // Determine if we should show loading state (same as BrowsePage)
  const showLoading = loading || (!isInitialized && !lastSearch)

  // Debug products state
  console.log('MerchantPage render state:', { 
    products: products.length, 
    loading, 
    dataLoading, 
    hasMore, 
    totalResults, 
    isInitialized, 
    lastSearch,
    showLoading
  })

  return (
    <div className='w-full min-h-screen'>
      {/* Back Button */}
      <div className='px-8 sm:px-16 py-4 max-w-8xl mx-auto flex items-center'>
        <BackButton />
      </div>
      
      {/* Profile Header Section */}
      <div className='relative'>
        {/* Banner background with improved gradient */}
        <div className='relative h-64 bg-gradient-to-r from-brand-teal to-teal-400'>
          <ProfileBackground user={merchant} isCurrentUser={false} />
        </div>

        {/* Profile Section Wrapper */}
        <div className="max-w-8xl mx-auto px-8 sm:px-16">
          <div className='relative'>
            {/* Avatar - ProfilePhoto handles its own absolute positioning */}
            <ProfilePhoto user={merchant} isCurrentUser={false} />
            
            {/* Profile info with stats on the right */}
            <div className='pt-20 pb-4 flex justify-between items-start'>
              <div>
                <h1 className='text-2xl font-bold'>{merchant.username}</h1>
                
                {/* Location */}
                {merchantLocation && merchantLocation.city && merchantLocation.country && (
                  <div className='mt-3 flex items-center text-sm text-gray-600'>
                    <MapPinIcon className='h-4 w-4 mr-1 text-brand-teal' />
                    <span>{merchantLocation.city}, {merchantLocation.country}</span>
                  </div>
                )}
                <UserRatingDisplay userId={id} ratingComponentSize="base" className="mt-2" />
              </div>
              
              {/* Stats positioned on the right side of profile info */}
              <div className='flex text-gray-700'>
                <div className='mr-8 text-center'>
                  <p className='text-base text-gray-500'>Followers</p>
                  <p className='text-xl font-bold text-brand-teal'>{merchant.followersCount || 0}</p>
                </div>
                <div className='text-center'>
                  <p className='text-base text-gray-500'>Following</p>
                  <p className='text-xl font-bold text-brand-teal'>{merchant.followingCount || 0}</p>
                </div>
              </div>
            </div>
            
            {/* Follow and Message Buttons - only show if logged in and not viewing own profile */}
            {isLoggedIn && currentUser?.id !== merchant.id && (
              <div className='mb-8 flex space-x-3'>
                <Button
                  variant={isCurrentUserFollowing ? 'outline' : 'teal'}
                  className='py-2 px-6'
                  onClick={handleFollowClick}
                  disabled={isFollowing}
                >
                  {isFollowing
                    ? '...'
                    : isCurrentUserFollowing
                    ? 'Following'
                    : 'Follow'}
                </Button>
                <Button
                  variant='outline'
                  className='py-2 px-6 flex items-center'
                  onClick={handleMessageClick}
                >
                  <ChatBubbleOvalLeftEllipsisIcon className='h-5 w-5 mr-2' />
                  Message
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products Section Wrapper */}
      <div className="max-w-8xl mx-auto px-8 sm:px-16">
        <div className='py-6'>
          <h2 className='text-xl font-semibold mb-4'>{merchant.activeProductsCount} Products</h2>
          
          {/* Filters */}
          <BrowseFilter />
          
          {/* Products Grid */}
          <div className='mt-6 bg-white shadow-sm rounded-lg p-6'>
            <CardsContainer
              loading={showLoading}
              error={error}
              hasMore={hasMore}
              totalResults={totalResults}
              searchTerm={searchTerm}
              dataLoading={dataLoading}
              isInitialized={isInitialized}
              lastSearch={lastSearch}
            >
              {products.map(product => (
                <BrowseCard key={product.id} product={product} />
              ))}
            </CardsContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MerchantPage
